import { useIsFocused } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { newListingSourceAtom } from 'app/atoms';
import { type ReviewSwipeAction, type SwipeableReviewCardHandle } from 'app/components/swipeable-review-card';
import { useNavigationOptions } from 'app/hooks/navigation';
import { Event, track } from 'app/lib/analytics';
import { getI18nLanguage } from 'app/lib/language';
import { useStatistics } from 'app/network/statistics';
import { getSuggestedListings } from 'app/network/suggested-listing';
import { useUser } from 'app/network/user';
import { canUseOfferHub } from 'app/screens/intro/navigator-helper';
import { useTheme } from 'app/shopify';
import { ListingType } from 'app/types/listing';
import type { NavigationScreen } from 'app/types/navigation';
import { DynamicFeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { Image as ExpoImage } from 'expo-image';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityInfo, Platform } from 'react-native';
import HapticFeedback from 'react-native-haptic-feedback';
import { useReducedMotion } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { v4 as uuid } from 'uuid';
import { type HubTab, type HubTutorialStep } from './components';
import { useOfferHubQueue } from './queue-provider';
import { refreshHubRound, remainingRoundItems, useHubSession, type HubRound } from './session';

import type { ComponentProps } from 'react';

export function useOfferHubState({ navigation, route }: ComponentProps<NavigationScreen<'offer-hub'>>) {
  const { t } = useTranslation('offer-hub');
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const focused = useIsFocused();
  const reducedMotion = useReducedMotion();
  const storedCreationSource = useAtomValue(newListingSourceAtom);
  const [creationSource] = useState(() => route.params?.newListingSource ?? storedCreationSource);
  const resetNewListingSource = useResetAtom(newListingSourceAtom);
  const userQuery = useUser();
  const user = userQuery.data;
  const hasAccess = canUseOfferHub(user?.featureFlags, userQuery.isPending);
  const session = useHubSession(hasAccess ? user?.id : undefined);
  const { save } = session;
  const { queue, ready, decisions, storageError } = useOfferHubQueue();
  const candidates = useQuery({
    queryKey: ['offer-hub', 'suggested-listings', user?.id, getI18nLanguage()],
    queryFn: async () => {
      await queue?.whenSettled();
      return getSuggestedListings();
    },
    enabled: Boolean(user?.id) && hasAccess && ready && Boolean(queue),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const [tab, setTab] = useState<HubTab>('suggestions');
  const [tutorialStep, setTutorialStep] = useState<HubTutorialStep>(0);
  const [replayingTutorial, setReplayingTutorial] = useState(false);
  const [swipeTutorialStep, setSwipeTutorialStep] = useState(0);
  const [replayingSwipeTutorial, setReplayingSwipeTutorial] = useState(false);
  const [active, setActive] = useState(false);
  const [round, setRound] = useState<HubRound | null>(null);
  const [fetchingRound, setFetchingRound] = useState(false);
  const [roundError, setRoundError] = useState(false);
  const swipeTutorialCard = useRef<SwipeableReviewCardHandle>(null);
  const [visitedTabs, setVisitedTabs] = useState<HubTab[]>([]);
  const selectTab = (selected: HubTab) => {
    if (selected === tab) return;
    track(Event.ChangeTab, {
      page_name: 'offerHub',
      tab: selected === 'suggestions' ? 'suggested' : selected === 'photos' ? 'bulk' : 'new',
    });
    setTab(selected);
    setVisitedTabs((tabs) => (tabs.includes(selected) ? tabs : [...tabs, selected]));
  };
  const manualRoute = {
    key: `${route.key}:manual`,
    name: 'new' as const,
    params: {
      ...route.params,
      newListingSource: creationSource,
      defaultValues: { ...route.params?.defaultValues, type: ListingType.offer },
    },
  };
  const bulkRoute = {
    key: `${route.key}:photos`,
    name: 'bulkNew' as const,
    params: { newListingSource: creationSource },
  };
  const swipeCard = useRef<SwipeableReviewCardHandle>(null);
  const committed = useRef(new Set<string>());
  const starting = useRef(false);
  const [wasFocused, setWasFocused] = useState(focused);
  const initialQueueErrorsCleared = useRef(false);
  const canUseAI = Boolean(user && isFeatureEnabled(DynamicFeatureFlag.BulkListingCreation, user.featureFlags));
  const items = round ? remainingRoundItems(round, decisions) : [];
  const currentItem = items[0];
  const firstPreviewPicture = items[0]?.listingGroup.picture;
  const secondPreviewPicture = items[1]?.listingGroup.picture;
  const thirdPreviewPicture = items[2]?.listingGroup.picture;
  const completed = Boolean(active && round?.items.length && !currentItem);
  const inProgress = Boolean(round && currentItem && items.length < round.items.length);
  const tutorial = !session.tutorialCompleted || replayingTutorial;
  const swiping = active && !completed && Boolean(currentItem) && !tutorial && !replayingSwipeTutorial;
  const swipeTutorialSwiping = replayingSwipeTutorial && (swipeTutorialStep === 1 || swipeTutorialStep === 2);
  const roundDecisions = decisions.filter((decision) => decision.roundId === round?.id);
  const accepted = roundDecisions.filter((decision) => decision.kind === 'accept' && decision.status === 'saved');
  const pending = roundDecisions.filter((decision) => decision.status === 'pending').length;
  const failed = decisions.filter((decision) => decision.status === 'failed');
  const statistics = useStatistics(user?.id ?? '', { enabled: hasAccess && completed });
  const { refetch: refreshStatistics } = statistics;

  useNavigationOptions(navigation, { headerShown: false });
  useEffect(() => () => resetNewListingSource(), [resetNewListingSource]);
  useEffect(() => {
    if (!focused || !ready || !queue || initialQueueErrorsCleared.current) return;
    initialQueueErrorsCleared.current = true;
    queue.clearErrors();
  }, [focused, queue, ready]);
  useEffect(() => {
    if (userQuery.isPending || hasAccess) return;
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('main', { screen: 'billboard' });
  }, [hasAccess, navigation, userQuery.isPending]);
  useEffect(() => {
    if (round || !ready || !session.ready || candidates.isFetching || candidates.isError || !candidates.data) return;
    const stored = session.round;
    const storedItems = stored ? remainingRoundItems(stored, decisions) : [];
    const seen = new Set(decisions.map((decision) => decision.suggestedListingId));
    const next =
      stored && storedItems.length
        ? refreshHubRound(stored, candidates.data, decisions)
        : { id: uuid(), items: (candidates.data ?? []).filter((item) => !seen.has(item.id)).slice(0, 10) };
    let cancelled = false;
    void Promise.resolve().then(() => {
      if (cancelled) return;
      setRound(next);
      void save({ round: next });
    });
    return () => {
      cancelled = true;
    };
  }, [
    candidates.data,
    candidates.isError,
    candidates.isFetching,
    decisions,
    ready,
    round,
    save,
    session.ready,
    session.round,
  ]);
  if (focused !== wasFocused) {
    setWasFocused(focused);
    if (focused) {
      setActive(false);
      setTutorialStep(0);
      setReplayingTutorial(false);
      setSwipeTutorialStep(0);
      setReplayingSwipeTutorial(false);
      if (round && !remainingRoundItems(round, decisions).length) setRound(null);
    }
  }
  useEffect(() => {
    const urls = [firstPreviewPicture, secondPreviewPicture, thirdPreviewPicture].filter((url): url is string =>
      Boolean(url),
    );
    if (urls.length) void ExpoImage.prefetch(urls).catch(() => undefined);
  }, [firstPreviewPicture, secondPreviewPicture, thirdPreviewPicture]);
  useEffect(() => {
    if (!completed) return;
    AccessibilityInfo.announceForAccessibility(t('listComplete'));
    if (Platform.OS !== 'web' && !reducedMotion) HapticFeedback.trigger('notificationSuccess');
  }, [completed, reducedMotion, t]);
  useEffect(() => {
    if (completed) void refreshStatistics();
  }, [accepted.length, completed, refreshStatistics]);

  const close = () => {
    if (navigation.canGoBack()) navigation.goBack();
    else navigation.navigate('main', { screen: 'billboard' });
  };
  const replayTutorial = () => {
    setTab('suggestions');
    setReplayingTutorial(true);
    setTutorialStep(0);
    setActive(false);
  };
  const replaySwipeTutorial = () => {
    setSwipeTutorialStep(0);
    setReplayingSwipeTutorial(true);
    setActive(false);
  };
  const dismissTutorial = () => {
    setTutorialStep(0);
    setReplayingTutorial(false);
    if (!session.tutorialCompleted) void save({ tutorialCompleted: true });
  };
  const decide = (kind: ReviewSwipeAction) => {
    if (!currentItem || !round || !queue || committed.current.has(currentItem.id)) return;
    const title = currentItem.listingGroup.name ?? '';
    if (
      !queue.enqueue({
        id: uuid(),
        roundId: round.id,
        suggestedListingId: currentItem.id,
        title,
        picture: currentItem.listingGroup.picture ?? '',
        kind,
        source: creationSource,
        status: 'pending',
        attempts: 0,
      })
    )
      return;
    committed.current.add(currentItem.id);
    if (Platform.OS !== 'web' && !reducedMotion) HapticFeedback.trigger('impactLight');
    // Also re-render synchronously when the outbox consumer has not notified React yet.
    setRound({ ...round });
  };
  const startList = async (fetchMore = false) => {
    if (starting.current) return;
    starting.current = true;
    setRoundError(false);
    const start = async () => {
      // Recheck campaign activation/expiry before displaying a saved round.
      setFetchingRound(true);
      await queue?.whenSettled();
      const result = await candidates.refetch();
      if (result.isError || !result.data) {
        setRoundError(true);
        return;
      }
      const next = refreshHubRound(
        fetchMore || !currentItem || !round ? { id: uuid(), items: [] } : round,
        result.data,
        queue?.getSnapshot().decisions ?? decisions,
      );
      if (!(await save({ round: next }))) return;
      setRound(next);
      setReplayingTutorial(false);
      setTutorialStep(0);
      setReplayingSwipeTutorial(false);
      setSwipeTutorialStep(0);
      setActive(true);
    };
    try {
      await start();
    } catch {
      setRoundError(true);
    }
    starting.current = false;
    setFetchingRound(false);
  };
  const tutorialVisible = tutorial;
  const helpTutorialVisible = tutorialVisible || replayingSwipeTutorial;
  const loaded = ready && session.ready && Boolean(round);
  const tutorialOverlayVisible: boolean = loaded && tutorialVisible;
  const overviewActive: boolean = tab === 'suggestions' && focused && !helpTutorialVisible;
  const showError = storageError || session.storageError;

  return {
    userQuery,
    hasAccess,
    insets,
    tab,
    tutorialOverlayVisible,
    tutorialStep,
    canUseAI,
    setTutorialStep,
    dismissTutorial,
    visitedTabs,
    focused,
    bulkRoute,
    manualRoute,
    navigation,
    setActive,
    setTab,
    showError,
    candidates,
    roundError,
    failed,
    queue,
    swipeTutorialStep,
    swipeTutorialCard,
    t,
    setSwipeTutorialStep,
    currentItem,
    fetchingRound,
    startList,
    accepted,
    statistics,
    refreshStatistics,
    pending,
    helpTutorialVisible,
    swiping,
    replayTutorial,
    items,
    round,
    selectTab,
    replaySwipeTutorial,
    close,
    session,
    tutorialVisible,
    swipeTutorialSwiping,
    loaded,
    replayingSwipeTutorial,
    completed,
    swipeCard,
    decide,
    theme,
    inProgress,
    overviewActive,
  };
}

export type OfferHubState = ReturnType<typeof useOfferHubState>;
